/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { combineReducers, configureStore, type Dispatch } from '@reduxjs/toolkit';
import { createEpicMiddleware } from 'redux-observable';

import { sarsaparillaReducers, store as sarsaparillaStore } from 'sarsaparilla';
import { authReducers } from 'shared-ui/auth/redux';

import searchAggregationsReducers from 'ui-search/src/reducers/searchAggregations';

import { rootEpic } from '../src/camping/epics';
import orderDetailsReducers from '../src/camping/reducers/orderDetails';
import recommendationReducer from '../src/camping/reducers/recommendations';
import reservationsReducers from '../src/camping/reducers/reservations';
import moduleStore from '../src/camping/store';
import {
    amenityReducers,
    availabilityGridReducers,
    campingAvailableCampsiteReducers,
    campingCampgroundReducers,
    campingCampsiteAvailabilityReducers,
    campingCampsiteReducers,
    campingGatewayReducers,
    campingLookupReducers,
    campingRecareaReducers,
    campingValidationReducers,
    continueToExternalLinkReducers,
    externalRatingReviewsReducers,
    inventoryReducer,
    mapBoxReducers,
    okefenokeeReducers,
    rentalsReducers,
} from '../src/shared';
import availabilityAlertReducers from '../src/shared/reducers/availabilityAlerts';
import digitalPassesReducer from '../src/shared/reducers/digitalPasses';
import earlyAccessLotteryReducers from '../src/shared/reducers/earlyAccessLottery';
import gisMapReducer from '../src/shared/reducers/gisMap';

const rootReducer = combineReducers({
    // camping module reducers
    recarea: campingRecareaReducers,
    availabilityGrid: availabilityGridReducers,
    campground: campingCampgroundReducers,
    campsiteAvailabilityDateRange: campingCampsiteAvailabilityReducers,
    availableCampsites: campingAvailableCampsiteReducers,
    campsite: campingCampsiteReducers,
    gisMap: gisMapReducer,
    reservations: reservationsReducers,
    orderDetails: orderDetailsReducers,
    recommendations: recommendationReducer,
    earlyAccessLottery: earlyAccessLotteryReducers,
    mapBox: mapBoxReducers,
    campingValidation: campingValidationReducers,
    gateway: campingGatewayReducers,
    ratingReviews: externalRatingReviewsReducers,
    lookup: campingLookupReducers,
    amenity: amenityReducers,
    continueToExternalLink: continueToExternalLinkReducers,
    okefenokee: okefenokeeReducers,
    inventory: inventoryReducer,
    availabilityAlerts: availabilityAlertReducers,
    digitalPasses: digitalPassesReducer,

    // search
    searchAggregations: searchAggregationsReducers,

    // rentals
    rentals: rentalsReducers,

    ...sarsaparillaReducers,

    // Auth - MUST COME AFTER SARSA REDUCERS DURING AUTH MIGRATION
    ...authReducers,
});

const initialState = {
    ...moduleStore(),
    ...sarsaparillaStore(),
};

export function setupStore(preloadedState?: any) {
    // redux-observable epics do not receive `dispatch`; they emit actions.
    // A handful of epics (e.g. submitOrderDetails' account-update stream) must
    // route session writes through shared-ui's `writeSession`, which needs a
    // real `dispatch`. Expose one as an epic dependency. It forwards to the
    // store, which does not exist yet at middleware-creation time, so we go
    // through a mutable reference that is set immediately after the store is
    // built and before any epic runs.
    let storeDispatch: Dispatch = () => {
        throw new Error('Camping epic dispatched before the store was initialized');
    };
    const dispatchForEpics: Dispatch = (action) => storeDispatch(action);

    const epicMiddleware = createEpicMiddleware({
        dependencies: { dispatch: dispatchForEpics },
    });

    const newStore = configureStore({
        reducer: rootReducer,
        middleware: (getDefaultMiddleware) => {
            return getDefaultMiddleware({
                immutableCheck: {
                    // TODO: need to figure out what's mutating in these reducers...
                    ignoredPaths: [
                        'availabilityGrid',
                        'campground',
                        'gisMap',
                        'orderDetails',
                        'campsite',
                    ],
                },
                serializableCheck: {
                    ignoredActions: [
                        'launchdarkly/setClient',
                        'COMPLETE_SUBMISSION_FAILURE',
                        'GET_CAMPGROUND_AVAILABILITY',
                        'GET_PENDING_RESERVATION_PRICE_DETAILS',
                        'SET_GRID_RANGE',
                        'GIS_SETUP_GEOCAMPSITES',
                        'PENDING_RESERVATION_PROP_CHANGE_REQUEST',
                        'UPDATE_AVAILABILITIES_COMPLETE',
                        'UPDATE_SINGLE_CAMPSITE_AVAILABILITIES_COMPLETE',
                        'UPDATE_SELECTIONS',
                        'UPDATE_SELECTION_BY_ID',
                        'UPDATE_SINGLE_CAMPSITE_AVAILABILITIES_COMPLETE',
                    ],
                    ignoredPaths: [
                        'launchdarkly', // Has ld client object in reducer
                        'availabilityGrid',
                        'gisMap',
                        'orderDetails',
                        'campsite',
                    ],
                },
            }).concat(epicMiddleware);
        },
        preloadedState,
        devTools: process.env.DEV_TOOLS_ENABLED,
    });

    storeDispatch = newStore.dispatch;
    epicMiddleware.run(rootEpic);

    return newStore;
}

export const store = setupStore(initialState);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppStore = typeof store;

// cSpell:ignore geocampsites, parkpass, recarea, reduxjs
